/**
 * About page module for OptiGold PMO Website
 * 
 * Displays company information, values, team members, and mission
 */

import React from 'react';
import { Award, CheckCircle, Users, Briefcase, Star, Shield, Building2, Building, Rocket, Sparkles, ArrowRight, ChevronDown } from "lucide-react";
import { Icon } from '@iconify/react';
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import SiteCTA from '@/components/SiteCTA';
import { motion, useScroll, useTransform } from 'framer-motion';

// Animated background component
const AnimatedBackground = () => {
  return (
    <div className="absolute inset-0 overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-black via-gray-900 to-black" />
      {/* Animated grid */}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff08_1px,transparent_1px),linear-gradient(to_bottom,#ffffff08_1px,transparent_1px)] bg-[size:24px_24px]" />
      {/* Floating orbs */}
      <motion.div
        className="absolute top-20 right-20 w-96 h-96 bg-gold/20 rounded-full blur-3xl"
        animate={{
          x: [0, 100, 0],
          y: [0, -50, 0],
        }}
        transition={{
          duration: 15,
          repeat: Infinity,
          ease: "easeInOut"
        }}
      />
      <motion.div
        className="absolute bottom-20 left-20 w-72 h-72 bg-amber-500/20 rounded-full blur-3xl"
        animate={{
          x: [0, -50, 0],
          y: [0, 100, 0],
        }}
        transition={{
          duration: 12,
          repeat: Infinity,
          ease: "easeInOut"
        }}
      />
      <motion.div
        className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-gold/10 rounded-full blur-3xl"
        animate={{
          scale: [1, 1.2, 1],
          opacity: [0.3, 0.5, 0.3],
        }}
        transition={{
          duration: 10,
          repeat: Infinity,
          ease: "easeInOut"
        }}
      />
    </div>
  );
};

/**
 * AboutPage component - Company information page
 * 
 * @returns React component for the about page
 */
const AboutPage = () => {
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 500], [0, -150]);
  const opacity = useTransform(scrollY, [0, 300], [1, 0]);
  const values = [
    {
      icon: 'solar:shield-check-bold',
      title: "Trust & Transparency",
      description: "We lead with honesty and integrity to build strong, lasting partnerships."
    },
    {
      icon: 'solar:users-group-two-rounded-bold',
      title: "Client-First Approach", 
      description: "Your goals drive everything we do—we listen, adapt, and deliver with care."
    },
    {
      icon: 'solar:case-round-bold',
      title: "Tailored Solutions",
      description: "Every solution is crafted to fit your culture, needs, and growth path."
    },
    {
      icon: 'solar:target-bold',
      title: "Clarity & Focus",
      description: "We simplify complexity so you can lead with confidence and deliver real results."
    },
    {
      icon: 'solar:graph-up-bold',
      title: "Sustainable Value",
      description: "We design for the long term—building PMOs that thrive long after we're gone."
    },
    {
      icon: 'solar:cup-star-bold',
      title: "Community Commitment",
      description: "We believe in giving back—sharing our time and expertise to support professional growth."
    }
  ];

  return (
    <div className="min-h-screen bg-black text-white overflow-hidden">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center">
        <AnimatedBackground />
        
        {/* Hero content */}
        <motion.div 
          style={{ y, opacity }}
          className="container mx-auto px-4 relative z-10 text-center"
        >
          {/* Animated badge */}
          <motion.div
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.5 }}
            className="inline-flex items-center gap-3 bg-gradient-to-r from-gold/20 to-amber-400/20 rounded-full px-6 py-3 mb-8 border border-gold/30 backdrop-blur-sm"
          >
            <Sparkles className="w-5 h-5 text-gold animate-pulse" />
            <span className="text-gold font-bold text-sm uppercase tracking-wider">About OptiGold PMO</span>
            <Sparkles className="w-5 h-5 text-gold animate-pulse" />
          </motion.div>

          {/* Main heading with staggered animation */}
          <motion.div className="mb-8">
            <motion.h1 
              className="text-5xl md:text-7xl lg:text-8xl font-bold leading-tight"
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-gold via-amber-300 to-gold animate-gradient">
                Elite PMO Expertise.
              </span>
              <motion.span 
                className="block text-white mt-4"
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.4 }}
              >
                Proven Results.
              </motion.span>
            </motion.h1>
          </motion.div>

          {/* Subtitle */}
          <motion.p 
            className="text-xl md:text-2xl text-gray-300 max-w-4xl mx-auto mb-12 leading-relaxed"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
          >
            We deliver personalized PMO solutions that transform challenges into competitive advantages.
          </motion.p>

        </motion.div>

        {/* Scroll indicator */}
        <motion.div
          className="absolute bottom-8 inset-x-0 flex justify-center"
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 1.5, repeat: Infinity }}
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
        >
            <div className="flex flex-col items-center gap-2 text-gold">
            <span className="text-sm uppercase tracking-wider">Scroll to explore</span>
            <ChevronDown className="w-6 h-6" />
          </div>
        </motion.div>
      </section>

      {/* Founder Section */}
      <section className="py-20 bg-gradient-to-b from-black via-gray-900 to-black relative">
        <div className="container mx-auto px-4">
          <motion.div 
            className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8 }}
          >
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <div className="relative">
                <div className="rounded-2xl overflow-hidden">
                  <img
                    src="/ceo-profile.jpg"
                    alt="Noah McCloud, Founder of OptiGold"
                    className="w-full h-auto"
                  />
                  <div className="absolute bottom-0 left-0 right-0 bg-black/70 p-3 text-center">
                    <motion.a 
                      href="https://www.linkedin.com/in/noah-mccloud-55219446?utm_source=share&utm_campaign=share_via&utm_content=profile&utm_medium=ios_app" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-white hover:text-gold transition-colors text-sm font-medium flex items-center justify-center"
                      whileHover={{ scale: 1.05 }}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="currentColor" className="mr-1">
                        <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/>
                      </svg>
                      Connect with Noah on LinkedIn
                    </motion.a>
                  </div>
                </div>
                
                {/* Experience badge */}
                <motion.div 
                  className="absolute -bottom-10 right-0 md:-right-10 bg-gradient-to-br from-gold to-amber-400 rounded-full shadow-2xl p-6 w-32 h-32 flex flex-col items-center justify-center border-4 border-gold/50"
                  initial={{ scale: 0, rotate: -180 }}
                  whileInView={{ scale: 1, rotate: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.8, delay: 0.5 }}
                  whileHover={{ scale: 1.1 }}
                >
                  <Award className="text-black mb-1" size={24} />
                  <span className="text-xl font-bold text-black">20+</span>
                  <span className="text-sm text-black font-semibold">Years Exp.</span>
                </motion.div>
                

              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              <motion.blockquote 
                className="relative border-l-4 border-gold pl-6 italic text-gray-300 mb-6 bg-gradient-to-r from-gold/10 to-transparent rounded-r-lg p-6"
                whileHover={{ scale: 1.02, x: 10 }}
              >
                <p className="text-lg font-bold text-white">
                  "Our clients benefit from highly personalized service and expert guidance without the overhead, complexity, or cost of large consulting firms and teams."
                </p>
                <footer className="mt-2 text-md text-gold font-semibold">
                  — Noah McCloud, Founder, OptiGold PMO Consulting
                </footer>
              </motion.blockquote>
              <motion.p 
                className="text-lg text-gray-300 mb-6"
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                OptiGold PMO Consulting was founded by Noah McCloud, a seasoned PMO leader with over 20 years of experience in project, program, and portfolio management across diverse industries, including healthcare, financial services, telecommunications, and transportation & mobility.
              </motion.p>
              <motion.p 
                className="text-lg text-gray-300 mb-6"
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.4 }}
              >
                Having spent his entire career leading diverse teams, day-to-day operations, and successful transformations, Noah recognized the power of a well-designed, tailored PMO, one that evolves beyond administrative tasks to become a strategic enabler and trusted partner within an organization. With this insight, he founded OptiGold PMO Consulting, driven by a passion for customer service, problem solving, and delivering personalized solutions that address real-world PMO challenges.
              </motion.p>
              <motion.p 
                className="text-lg text-gray-300 mb-6"
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.6 }}
              >
                At OptiGold PMO, we partner with organizations to solve their most pressing project, program, portfolio, and PMO pain points. Our clients benefit from highly personalized service and expert guidance without the overhead, complexity, or cost of large consulting firms and teams.
              </motion.p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Why Businesses Trust OptiGold PMO Section */}
      <section className="py-20 bg-gradient-to-b from-black via-gray-900 to-black relative">
        <div className="container mx-auto px-4">
          <div className="max-w-7xl mx-auto">
            <motion.div 
              className="text-center mb-16"
              initial={{ opacity: 0, y: -20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl md:text-6xl font-bold mb-6">
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-gold to-amber-300">
                  Why Businesses Trust OptiGold PMO
                </span>
              </h2>
            </motion.div>

            {/* Main Content Grid */}
            <div className="relative">
              {/* Background decoration */}
              <div className="absolute inset-0 bg-gradient-to-r from-gold/10 to-transparent rounded-3xl"></div>
              
              <div className="relative grid grid-cols-1 lg:grid-cols-2 gap-12 p-8 lg:p-12">
                {/* Left Column - Statistics */}
                <div className="space-y-8">
                  <motion.div 
                    className="bg-gradient-to-br from-gray-900/80 to-black/80 backdrop-blur-xl rounded-xl p-6 border border-gold/20 hover:border-gold/40 transition-all duration-300"
                    initial={{ opacity: 0, x: -30 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.5, delay: 0.1 }}
                    viewport={{ once: true }}
                    whileHover={{ scale: 1.05, boxShadow: "0 20px 40px rgba(212, 175, 55, 0.2)" }}
                  >
                    <div className="flex items-start space-x-4">
                      <div className="flex-shrink-0">
                        <motion.div 
                          className="w-12 h-12 bg-gradient-to-br from-gold to-amber-400 rounded-full flex items-center justify-center"
                          whileHover={{ rotate: 360 }}
                          transition={{ duration: 0.5 }}
                        >
                          <CheckCircle className="text-black" size={24} />
                        </motion.div>
                      </div>
                      <div>
                        <p className="text-gray-300">
                          <span className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-gold to-amber-400">20+ Years</span> project, program, portfolio & leadership experience
                        </p>
                      </div>
                    </div>
                  </motion.div>

                  <motion.div 
                    className="bg-gradient-to-br from-gray-900/80 to-black/80 backdrop-blur-xl rounded-xl p-6 border border-gold/20 hover:border-gold/40 transition-all duration-300"
                    initial={{ opacity: 0, x: -30 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.5, delay: 0.2 }}
                    viewport={{ once: true }}
                    whileHover={{ scale: 1.05, boxShadow: "0 20px 40px rgba(212, 175, 55, 0.2)" }}
                  >
                    <div className="flex items-start space-x-4">
                      <div className="flex-shrink-0">
                        <motion.div 
                          className="w-12 h-12 bg-gradient-to-br from-gold to-amber-400 rounded-full flex items-center justify-center"
                          whileHover={{ rotate: 360 }}
                          transition={{ duration: 0.5 }}
                        >
                          <CheckCircle className="text-black" size={24} />
                        </motion.div>
                      </div>
                      <div>
                        <p className="text-gray-300">
                          <span className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-gold to-amber-400">$600M+</span> in project and portfolio delivery
                        </p>
                      </div>
                    </div>
                  </motion.div>

                  <motion.div 
                    className="bg-gradient-to-br from-gray-900/80 to-black/80 backdrop-blur-xl rounded-xl p-6 border border-gold/20 hover:border-gold/40 transition-all duration-300"
                    initial={{ opacity: 0, x: -30 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.5, delay: 0.3 }}
                    viewport={{ once: true }}
                    whileHover={{ scale: 1.05, boxShadow: "0 20px 40px rgba(212, 175, 55, 0.2)" }}
                  >
                    <div className="flex items-start space-x-4">
                      <div className="flex-shrink-0">
                        <motion.div 
                          className="w-12 h-12 bg-gradient-to-br from-gold to-amber-400 rounded-full flex items-center justify-center"
                          whileHover={{ rotate: 360 }}
                          transition={{ duration: 0.5 }}
                        >
                          <CheckCircle className="text-black" size={24} />
                        </motion.div>
                      </div>
                      <div>
                        <p className="text-gray-300">
                          <span className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-gold to-amber-400">50+</span> professionals lead & managed across four distinct PMOs
                        </p>
                      </div>
                    </div>
                  </motion.div>

                  <motion.div 
                    className="bg-gradient-to-br from-gray-900/80 to-black/80 backdrop-blur-xl rounded-xl p-6 border border-gold/20 hover:border-gold/40 transition-all duration-300"
                    initial={{ opacity: 0, x: -30 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.5, delay: 0.4 }}
                    viewport={{ once: true }}
                    whileHover={{ scale: 1.05, boxShadow: "0 20px 40px rgba(212, 175, 55, 0.2)" }}
                  >
                    <div className="flex items-start space-x-4">
                      <div className="flex-shrink-0">
                        <motion.div 
                          className="w-12 h-12 bg-gradient-to-br from-gold to-amber-400 rounded-full flex items-center justify-center"
                          whileHover={{ rotate: 360 }}
                          transition={{ duration: 0.5 }}
                        >
                          <CheckCircle className="text-black" size={24} />
                        </motion.div>
                      </div>
                      <div>
                        <p className="text-gray-300">
                          <span className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-gold to-amber-400">8+</span> Years of high-performance rating in PMO roles @ $37B org.
                        </p>
                      </div>
                    </div>
                  </motion.div>
                </div>

                {/* Right Column - Value Propositions */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 content-start">
                  <motion.div
                    className="bg-gradient-to-br from-gray-900/80 to-black/80 backdrop-blur-xl rounded-xl p-6 border border-gold/20 hover:border-gold/40 transition-all duration-300"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.2 }}
                    viewport={{ once: true }}
                    whileHover={{ y: -5 }}
                  >
                    <h4 className="font-bold text-xl mb-3 text-white">Tailored Approach</h4>
                    <p className="text-gray-400">Simplified solutions designed for your unique needs.</p>
                  </motion.div>

                  <motion.div
                    className="bg-gradient-to-br from-gray-900/80 to-black/80 backdrop-blur-xl rounded-xl p-6 border border-gold/20 hover:border-gold/40 transition-all duration-300"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.3 }}
                    viewport={{ once: true }}
                    whileHover={{ y: -5 }}
                  >
                    <h4 className="font-bold text-xl mb-3 text-white">End-To-End Support</h4>
                    <p className="text-gray-400">We're with you at every stage.</p>
                  </motion.div>

                  <motion.div
                    className="bg-gradient-to-br from-gray-900/80 to-black/80 backdrop-blur-xl rounded-xl p-6 border border-gold/20 hover:border-gold/40 transition-all duration-300"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.4 }}
                    viewport={{ once: true }}
                    whileHover={{ y: -5 }}
                  >
                    <h4 className="font-bold text-xl mb-3 text-white">Trusted Partnership</h4>
                    <p className="text-gray-400">Relationship-First approach.</p>
                  </motion.div>

                  <motion.div
                    className="bg-gradient-to-br from-gray-900/80 to-black/80 backdrop-blur-xl rounded-xl p-6 border border-gold/20 hover:border-gold/40 transition-all duration-300"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.5 }}
                    viewport={{ once: true }}
                    whileHover={{ y: -5 }}
                  >
                    <h4 className="font-bold text-xl mb-3 text-white">Cultural Alignment</h4>
                    <p className="text-gray-400">Services matched to your budget, pace, and operational style.</p>
                  </motion.div>

                  <motion.div
                    className="bg-gradient-to-br from-gray-900/80 to-black/80 backdrop-blur-xl rounded-xl p-6 border border-gold/20 hover:border-gold/40 transition-all duration-300"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.6 }}
                    viewport={{ once: true }}
                    whileHover={{ y: -5 }}
                  >
                    <h4 className="font-bold text-xl mb-3 text-white">Long-Term Impact</h4>
                    <p className="text-gray-400">Built for sustainability and continuous improvement.</p>
                  </motion.div>

                  <motion.div
                    className="bg-gradient-to-br from-gray-900/80 to-black/80 backdrop-blur-xl rounded-xl p-6 border border-gold/20 hover:border-gold/40 transition-all duration-300"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.7 }}
                    viewport={{ once: true }}
                    whileHover={{ y: -5 }}
                  >
                    <h4 className="font-bold text-xl mb-3 text-white">Agile & Adaptive</h4>
                    <p className="text-gray-400">Flexible support that evolves with your business.</p>
                  </motion.div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Mission & Who We Serve Section */}
      <section className="py-16 bg-gradient-to-b from-black via-gray-900 to-black">
        <div className="container mx-auto px-4 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Our Mission */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
              viewport={{ once: true, margin: "-100px" }}
              className="text-center"
            >
              <h2 className="text-3xl md:text-4xl font-bold mb-8 relative inline-block text-white">
                Our Mission
                <motion.span 
                  className="absolute -bottom-3 left-0 right-0 h-1 bg-gold"
                  initial={{ width: 0 }}
                  whileInView={{ width: "100%" }}
                  transition={{ duration: 0.8, delay: 0.3 }}
                  viewport={{ once: true }}
                ></motion.span>
              </h2>
              <p className="text-lg text-gray-300">
                Our mission is to help organizations build, elevate, and optimize PMOs that drive project success, inspire executive confidence, and enable long-term value. We deliver tailored solutions rooted in strategic vision and tactical expertise, partnering closely with clients to align PMO strategy with business goals and meet real-world project, program, portfolio and PMO challenges head-on.
              </p>
            </motion.div>

            {/* Who We Serve */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.2 }}
              viewport={{ once: true, margin: "-100px" }}
              className="text-center"
            >
              <h2 className="text-3xl md:text-4xl font-bold mb-8 relative inline-block text-white">
                Who We Serve
                <motion.span 
                  className="absolute -bottom-3 left-0 right-0 h-1 bg-gold"
                  initial={{ width: 0 }}
                  whileInView={{ width: "100%" }}
                  transition={{ duration: 0.8, delay: 0.5 }}
                  viewport={{ once: true }}
                ></motion.span>
              </h2>
              
              <div className="space-y-8">
                <motion.div 
                  className="text-center"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.3 }}
                  viewport={{ once: true }}
                >
                  <Building2 className="text-gold mx-auto mb-3" size={40} />
                  <h3 className="text-xl font-bold mb-2 text-white">Enterprise PMO's</h3>
                  <p className="text-gray-400">Large Scale, Complex Operations</p>
                </motion.div>

                <motion.div 
                  className="text-center"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.4 }}
                  viewport={{ once: true }}
                >
                  <Building className="text-gold mx-auto mb-3" size={40} />
                  <h3 className="text-xl font-bold mb-2 text-white">Mid-Market PMO's</h3>
                  <p className="text-gray-400">Rapid Growth & Scaling</p>
                </motion.div>

                <motion.div 
                  className="text-center"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.5 }}
                  viewport={{ once: true }}
                >
                  <Rocket className="text-gold mx-auto mb-3" size={40} />
                  <h3 className="text-xl font-bold mb-2 text-white">Small & Startup PMO's</h3>
                  <p className="text-gray-400">Establishing Foundations</p>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section className="py-20 bg-gradient-to-b from-black via-gray-900 to-black">
        <div className="container mx-auto px-4">
          <motion.div 
            className="text-center max-w-3xl mx-auto mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            viewport={{ once: true, amount: 0.3 }}
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-8 relative inline-block text-white">
              Our Core Values
              <motion.span 
                className="absolute -bottom-3 left-0 right-0 h-1 bg-gold"
                initial={{ width: 0 }}
                whileInView={{ width: "100%" }}
                transition={{ duration: 0.8, delay: 0.3 }}
                viewport={{ once: true }}
              ></motion.span>
            </h2>

          </motion.div>

          <motion.div 
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mt-12"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ 
              duration: 0.5,
              staggerChildren: 0.1,
              ease: [0.25, 0.1, 0.25, 1.0]
            }}
            viewport={{ once: true, amount: 0.2 }}
          >
            {values.map((value, index) => {
              // Calculate column position (0, 1, or 2) based on index for responsive layout
              const columnPosition = index % 3;
              
              return (
                <motion.div 
                  key={index} 
                  className="p-7 rounded-xl border border-gold/20 flex items-start space-x-5 bg-gradient-to-br from-gray-900/80 to-black/80 backdrop-blur-xl relative overflow-hidden transition-all duration-300 group hover:border-gold/40"
                  variants={{
                    hidden: { opacity: 0, y: 30 },
                    visible: { opacity: 1, y: 0 }
                  }}
                  initial="hidden"
                  whileInView="visible"
                  whileHover={{ 
                    y: -5,
                    boxShadow: "0 20px 40px rgba(212, 175, 55, 0.2)" 
                  }}
                  viewport={{ once: true }}
              >
                {/* Subtle bottom reflection */}
                <div className="absolute inset-x-0 bottom-0 h-[1px] bg-gold/10"></div>
                <div className="flex-shrink-0">
                  <div className="h-14 w-14 rounded-full flex items-center justify-center relative">
                    {/* High-gloss gold background matching CTA heading */}
                    <div className="absolute inset-0 rounded-full bg-gradient-to-r from-gold to-amber-300"></div>
                    {/* Icon with black color */}
                    <Icon icon={value.icon} width={28} height={28} className="text-black relative z-10" />
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-serif font-semibold mb-2 group-hover:text-gold transition-colors duration-300 text-white">{value.title}</h3>
                  <p className="text-gray-300 leading-relaxed">{value.description}</p>
                </div>
              </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* Recommendations Section */}
      <section className="py-20 bg-gradient-to-b from-black via-gray-900 to-black">
        <div className="container mx-auto px-4">
          <motion.div 
            className="text-center max-w-3xl mx-auto mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
          >
            <div className="text-center mb-4">
              <h2 className="text-3xl md:text-4xl font-bold relative inline-block text-white">
                Case Studies
                <motion.span 
                  className="absolute -bottom-3 left-0 right-0 h-1 bg-gold"
                  initial={{ width: 0 }}
                  whileInView={{ width: "100%" }}
                  transition={{ duration: 0.8, delay: 0.3 }}
                  viewport={{ once: true }}
                ></motion.span>
              </h2>
            </div>

          </motion.div>

          {/* Case Study Testimonial */}
          <motion.div 
            className="flex justify-center mb-16"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ 
              duration: 0.5,
              ease: [0.25, 0.1, 0.25, 1.0]
            }}
            viewport={{ once: true, amount: 0.1 }}
          >
            {/* PSG Case Study */}
            <motion.div 
              className="p-8 rounded-xl border border-gold/20 bg-gradient-to-br from-gray-900/80 to-black/80 backdrop-blur-xl relative overflow-hidden transition-all duration-300 max-w-2xl hover:border-gold/40"
              variants={{
                hidden: { opacity: 0, y: 30 },
                visible: { opacity: 1, y: 0 }
              }}
              initial="hidden"
              whileInView="visible"
              whileHover={{ 
                y: -5,
                boxShadow: "0 20px 40px rgba(212, 175, 55, 0.2)" 
              }}
              viewport={{ once: true }}
            >
              <div className="flex items-center mb-6">
                <div className="h-16 w-16 mr-4 flex items-center justify-center bg-white rounded shadow-sm">
                  <img src="/psg-logo.jpg" alt="Performance Safety Group Logo" className="h-12 w-auto object-contain" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white">Performance Safety Group, Inc.</h3>
                  <p className="text-gray-300">PPE Manufacturer & Distributor</p>
                  <p className="text-sm text-gold">Lightweight PMO Implementation</p>
                </div>
              </div>
              <div className="relative">
                <svg className="absolute -top-2 -left-2 text-gold/20 w-10 h-10" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z" />
                </svg>
                <p className="text-gray-300 leading-relaxed pl-6 mb-4">
                  "Working with OptiGold PMO to implement the lightweight PMO framework has been transformative for our organization. It has brought clarity to our project processes and enhanced collaboration across departments. The balance between structure and flexibility has allowed us to scale our operations effectively while maintaining our agility."
                </p>
                <p className="text-right text-gold font-medium">— Chris Sanders, CEO</p>
              </div>
              <div className="mt-6 pt-4 border-t border-gold/20">
                <p className="text-sm text-gray-300 mb-2"><span className="font-medium">Results:</span> 30% reduction in project cycle times, 80% improved visibility, 10% increase in resource utilization</p>
                <div className="text-right">
                  <a 
                    href="https://blog.optigoldpmo.com/2025/06/04/client-success-story-performance-safety-group-inc-psg/" 
                    className="inline-flex items-center text-gold hover:text-amber-400 transition-colors font-medium text-sm"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    View Case Study
                    <svg xmlns="http://www.w3.org/2000/svg" className="ml-1" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="7" y1="17" x2="17" y2="7"></line><polyline points="7 7 17 7 17 17"></polyline></svg>
                  </a>
                </div>
              </div>
            </motion.div>
          </motion.div>

          {/* Recommendations Section Header */}
          <motion.div 
            className="text-center max-w-3xl mx-auto mb-16 mt-20"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
          >
            <div className="text-center mb-4">
              <h2 className="text-3xl md:text-4xl font-bold relative inline-block text-white">
                Recommendations
                <motion.span 
                  className="absolute -bottom-3 left-0 right-0 h-1 bg-gold"
                  initial={{ width: 0 }}
                  whileInView={{ width: "100%" }}
                  transition={{ duration: 0.8, delay: 0.3 }}
                  viewport={{ once: true }}
                ></motion.span>
              </h2>
            </div>
          </motion.div>
          
          {/* Team Member Testimonials */}
          <motion.div 
            className="grid grid-cols-1 md:grid-cols-2 gap-8"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ 
              duration: 0.5,
              staggerChildren: 0.2,
              ease: [0.25, 0.1, 0.25, 1.0]
            }}
            viewport={{ once: true, amount: 0.1 }}
          >
            {/* Testimonial 1 - Lea Kinne */}
            <motion.div 
              className="p-8 rounded-xl border border-gold/20 bg-gradient-to-br from-gray-900/80 to-black/80 backdrop-blur-xl relative overflow-hidden transition-all duration-300 hover:border-gold/40"
              variants={{
                hidden: { opacity: 0, y: 30 },
                visible: { opacity: 1, y: 0 }
              }}
              initial="hidden"
              whileInView="visible"
              whileHover={{ 
                y: -5,
                boxShadow: "0 20px 40px rgba(212, 175, 55, 0.2)" 
              }}
              viewport={{ once: true }}
            >
              <div className="flex items-center mb-6">
                <div className="w-16 h-16 rounded-full overflow-hidden mr-4 border-2 border-gold shadow-md">
                  <img src="/Lea Kinne.jpeg" alt="Lea Kinne" className="w-full h-full object-cover" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white">Lea Kinne</h3>
                  <p className="text-gray-300">IT Project Manager Senior at Enterprise Mobility</p>
                  <p className="text-sm text-gold italic">Reported directly to Noah</p>
                </div>
              </div>
              <div className="relative">
                <svg className="absolute -top-2 -left-2 text-gold/20 w-10 h-10" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z" />
                </svg>
                <p className="text-gray-300 leading-relaxed pl-6">
                  "His leadership style strikes the perfect balance between vision and empathy. What stands out most is his ability to navigate complex challenges with calm confidence, offering actionable solutions while keeping stakeholders aligned. His deep knowledge of project management principles, combined with his natural leadership skills, makes him uniquely positioned to guide organizations toward successful outcomes."
                </p>
              </div>
            </motion.div>

            {/* Testimonial 2 - Gary Nelson */}
            <motion.div 
              className="p-8 rounded-xl border border-gold/20 bg-gradient-to-br from-gray-900/80 to-black/80 backdrop-blur-xl relative overflow-hidden transition-all duration-300 hover:border-gold/40"
              variants={{
                hidden: { opacity: 0, y: 30 },
                visible: { opacity: 1, y: 0 }
              }}
              initial="hidden"
              whileInView="visible"
              whileHover={{ 
                y: -5,
                boxShadow: "0 20px 40px rgba(212, 175, 55, 0.2)" 
              }}
              viewport={{ once: true }}
            >
              <div className="flex items-center mb-6">
                <div className="w-16 h-16 rounded-full overflow-hidden mr-4 border-2 border-gold shadow-md">
                  <img src="/Gary Nelson.jpeg" alt="Gary Nelson" className="w-full h-full object-cover" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white">Gary Nelson</h3>
                  <p className="text-gray-300">Global Corporate Development at Enterprise Mobility</p>
                  <p className="text-sm text-gold italic">Managed Noah directly</p>
                </div>
              </div>
              <div className="relative">
                <svg className="absolute -top-2 -left-2 text-gold/20 w-10 h-10" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z" />
                </svg>
                <p className="text-gray-300 leading-relaxed pl-6">
                  "Noah is smart, energetic, driven, organized, engaging, positive, and enthusiastic. He exceled at developing and fostering relationships with sponsors and colleagues. His adaptability and resilience under stress enabled him to remain composed while delivering quality results under high pressure. Throughout this time, Noah consistently demonstrated superb skills in resource management, strategic insight, leadership presence, program/project management, PMO governance, and process development."
                </p>
              </div>
            </motion.div>

            {/* Testimonial 3 - Kristen Scott */}
            <motion.div 
              className="p-8 rounded-xl border border-gold/20 bg-gradient-to-br from-gray-900/80 to-black/80 backdrop-blur-xl relative overflow-hidden transition-all duration-300 hover:border-gold/40"
              variants={{
                hidden: { opacity: 0, y: 30 },
                visible: { opacity: 1, y: 0 }
              }}
              initial="hidden"
              whileInView="visible"
              whileHover={{ 
                y: -5,
                boxShadow: "0 20px 40px rgba(212, 175, 55, 0.2)" 
              }}
              viewport={{ once: true }}
            >
              <div className="flex items-center mb-6">
                <div className="w-16 h-16 rounded-full overflow-hidden mr-4 border-2 border-gold shadow-md">
                  <img src="/Kristen Scott.jpeg" alt="Kristen Scott" className="w-full h-full object-cover" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white">Kristen Scott</h3>
                  <p className="text-gray-300">Manager, Digital Workplace Engineering at Enterprise Mobility</p>
                  <p className="text-sm text-gold italic">Reported directly to Noah</p>
                </div>
              </div>
              <div className="relative">
                <svg className="absolute -top-2 -left-2 text-gold/20 w-10 h-10" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z" />
                </svg>
                <p className="text-gray-300 leading-relaxed pl-6">
                  "Noah always challenged his team to be forward-thinking and process driven. Over the course of several years, I observed Noah partner with leadership to transform our PMO based on the long-term strategic vision of our organization. Noah prioritized pursuing a thoughtful approach to change management for not only his individual team, but also for the PMO and business as a whole. He leads with a people-centric approach whether in his partnerships with leadership or managing his direct reports."
                </p>
              </div>
            </motion.div>

            {/* Testimonial 4 - Gussie Fava */}
            <motion.div 
              className="p-8 rounded-xl border border-gold/20 bg-gradient-to-br from-gray-900/80 to-black/80 backdrop-blur-xl relative overflow-hidden transition-all duration-300 hover:border-gold/40"
              variants={{
                hidden: { opacity: 0, y: 30 },
                visible: { opacity: 1, y: 0 }
              }}
              initial="hidden"
              whileInView="visible"
              whileHover={{ 
                y: -5,
                boxShadow: "0 20px 40px rgba(212, 175, 55, 0.2)" 
              }}
              viewport={{ once: true }}
            >
              <div className="flex items-center mb-6">
                <div className="w-16 h-16 rounded-full overflow-hidden mr-4 border-2 border-gold shadow-md">
                  <img src="/Gussie Fava.jpeg" alt="Gussie Fava" className="w-full h-full object-cover" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white">Gussie Fava, PMP</h3>
                  <p className="text-gray-300">Senior Internal Communications Specialist at Enterprise Mobility</p>
                  <p className="text-sm text-gold italic">Reported directly to Noah</p>
                </div>
              </div>
              <div className="relative">
                <svg className="absolute -top-2 -left-2 text-gold/20 w-10 h-10" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z" />
                </svg>
                <p className="text-gray-300 leading-relaxed pl-6">
                  "Noah is a fantastic and visionary leader in our PMO. His strategic thinking and thoughtful execution led to countless successes as we work to redesign the PMO structure, standardize processes, and increase collaboration throughout the entire organization. As a manager, he is a very valuable mentor and provides timely and constructive feedback. His positive personality and extensive knowledge makes him a true leader both within and outside of the PMO."
                </p>
              </div>
            </motion.div>
          </motion.div>
          
          <div className="text-center mt-10">
            <p className="text-xs text-gray-400 italic">
              <span className="font-medium">DISCLAIMER:</span> The views expressed are those of the individual and do not necessarily represent the views of their employer.
            </p>
          </div>
        </div>
      </section>

      <SiteCTA />
    </div>
  );
};

export default AboutPage;
